import * as THREE from 'three'
import type { ImportedBodyEntry } from './glb'
import type {
  ImportedPartBodyBrepBinding,
  ImportedPartBodyTopologyAsset,
} from './types'
import { areImportedBodyBindingIdsEquivalent } from '../utils'

/** 导入 body 的模型体身份来源。 */
interface ImportedBodyModelIdentity {
  /** 可见 mesh/装配 occurrence 绑定 ID。 */
  readonly bodyBindingId: string
  /** body 语义 ID。 */
  readonly bodyId: string
  /** Worker GeometryState 中的权威几何对象 ID。 */
  readonly geometryObjectId?: string
}

/**
 * 按 bodyBindingId 解析拓扑资产。
 *
 * @param bodyBindingId 当前 BRep 绑定 ID。
 * @param bodyTopologyAssets body 拓扑资产表。
 * @returns 与绑定对应的拓扑资产；未命中时返回 undefined。
 */
function resolveBodyTopologyAsset(
  bodyBindingId: string,
  bodyTopologyAssets: Readonly<Record<string, ImportedPartBodyTopologyAsset>>,
): ImportedPartBodyTopologyAsset | undefined {
  return bodyTopologyAssets[bodyBindingId]
    ?? Object.values(bodyTopologyAssets).find((asset) => (
      areImportedBodyBindingIdsEquivalent(asset.bodyBindingId, bodyBindingId)
    ))
}

/**
 * 构建导入 body 的统一模型体身份表。
 *
 * @param bodyBrepBindings body 级 BRep 绑定表。
 * @param bodyTopologyAssets body 拓扑资产表。
 * @returns 可按 bodyId 或 binding 精确回填到 bodyEntries 的身份列表。
 */
function buildImportedBodyModelIdentities(
  bodyBrepBindings: Readonly<Record<string, ImportedPartBodyBrepBinding>>,
  bodyTopologyAssets: Readonly<Record<string, ImportedPartBodyTopologyAsset>>,
): ImportedBodyModelIdentity[] {
  return Object.values(bodyBrepBindings).map((binding) => {
    const topologyAsset = resolveBodyTopologyAsset(binding.bodyBindingId, bodyTopologyAssets)
    return {
      bodyBindingId: binding.bodyBindingId,
      bodyId: binding.bodyId?.trim() || binding.bodyBindingId,
      geometryObjectId: topologyAsset?.geometryObjectId?.trim() || undefined,
    }
  })
}

/**
 * 为单个实体条目解析唯一模型体身份。
 *
 * @param entry 原始实体条目。
 * @param identities 当前拓扑源内的模型体身份。
 * @returns 唯一命中的模型体身份；无法消歧时返回 undefined。
 */
function resolveImportedBodyModelIdentity(
  entry: ImportedBodyEntry,
  identities: readonly ImportedBodyModelIdentity[],
): ImportedBodyModelIdentity | undefined {
  const bodyBindingId = entry.bodyBindingId?.trim()
  const bindingMatches = bodyBindingId
    ? identities.filter((identity) => (
      areImportedBodyBindingIdsEquivalent(identity.bodyBindingId, bodyBindingId)
    ))
    : []
  if (bindingMatches.length === 1) {
    return bindingMatches[0]
  }

  const bodyId = entry.bodyId.trim()
  const bodyMatches = identities.filter((identity) => identity.bodyId === bodyId)
  return bodyMatches.length === 1 ? bodyMatches[0] : undefined
}

/**
 * 为导入 bodyEntries 回填统一模型体身份。
 *
 * 设计说明：
 * 1. GLB parser 只提供 bodyId/label，bodyBindingId 需由 bodyBrepBindings 反查；
 * 2. geometryObjectId 只读取 development 的 BRep 注册结果，不在显示层重新生成；
 * 3. 同一 body 无法唯一命中时不猜测，避免把兄弟 occurrence 或共享 mesh 绑定到错误修订。
 *
 * @param bodyEntries 原始 bodyEntries。
 * @param bodyBrepBindings body 级 BRep 绑定表。
 * @param bodyTopologyAssets body 拓扑资产表。
 * @returns 回填统一模型体身份后的 bodyEntries。
 */
export function enrichBodyEntriesWithModelBodyIdentities(
  bodyEntries: readonly ImportedBodyEntry[],
  bodyBrepBindings: Readonly<Record<string, ImportedPartBodyBrepBinding>>,
  bodyTopologyAssets: Readonly<Record<string, ImportedPartBodyTopologyAsset>> = {},
): ImportedBodyEntry[] {
  const identities = buildImportedBodyModelIdentities(bodyBrepBindings, bodyTopologyAssets)

  return bodyEntries.map((entry) => {
    const identity = resolveImportedBodyModelIdentity(entry, identities)
    if (!identity) {
      return entry
    }
    const bodyBindingId = entry.bodyBindingId?.trim()
    return {
      ...entry,
      bodyBindingId: bodyBindingId || identity.bodyBindingId,
      geometryObjectId: identity.geometryObjectId || entry.geometryObjectId?.trim(),
    }
  })
}

/**
 * 将统一模型体身份回填到场景节点 userData.importedPartBodyEntries。
 *
 * @param root 导入几何模板根节点。
 * @param bodyBrepBindings body 级 BRep 绑定表。
 * @param bodyTopologyAssets body 拓扑资产表。
 */
export function enrichSceneBodyEntriesWithModelBodyIdentities(
  root: THREE.Object3D,
  bodyBrepBindings: Readonly<Record<string, ImportedPartBodyBrepBinding>>,
  bodyTopologyAssets: Readonly<Record<string, ImportedPartBodyTopologyAsset>> = {},
): void {
  root.traverse((object) => {
    const rawEntries = object.userData.importedPartBodyEntries
    if (!Array.isArray(rawEntries) || rawEntries.length === 0) {
      return
    }

    object.userData.importedPartBodyEntries = enrichBodyEntriesWithModelBodyIdentities(
      rawEntries as ImportedBodyEntry[],
      bodyBrepBindings,
      bodyTopologyAssets,
    )
  })
}
